import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Link, Unlink, Lock, Unlock, History, Search, X } from 'lucide-react';
import {
  useTranche,
  useUpdateTranche,
  useDeleteTranche,
  useDeleteMovement,
  useLinkTransactionToMovement,
  useUnlinkTransactionFromMovement,
} from '../hooks/useAssociateAccounts.js';
import { useTransactions } from '../hooks/useTransactions.js';
import AssociateTrancheForm from '../components/AssociateTrancheForm.jsx';
import AssociateMovementForm from '../components/AssociateMovementForm.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Button from '../components/Button.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';

// ── Libellés des types de mouvement ──────────────────────────────────────────

const KIND_LABELS = {
  contribution:    'Apport',
  withdrawal:      'Retrait',
  adjustment:      'Régularisation',
  opening_balance: 'Reprise d\'antériorité',
};

const KIND_COLOR = {
  contribution:    'text-emerald-700',
  withdrawal:      'text-red-600',
  adjustment:      'text-amber-700',
  opening_balance: 'text-sky-700',
};

// Signe affiché pour le montant selon le kind
function signedAmount(movement) {
  const cents = movement.amount_cents ?? 0;
  const isPositive = movement.kind === 'contribution' || movement.kind === 'opening_balance';
  return isPositive ? cents : -cents;
}

// ── Link transaction picker (slide-over with searchable list) ───────────────

function shiftDate(isoDate, days) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function LinkTransactionModal({ open, onClose, onLink, movement }) {
  // Pré-filtres dérivés du mouvement (période ± 30 jours, signe attendu)
  const expectsPositive = movement?.kind === 'contribution' || movement?.kind === 'opening_balance';
  const movementDate = movement?.movement_date;

  const [from,   setFrom]   = useState(() => shiftDate(movementDate, -30));
  const [to,     setTo]     = useState(() => shiftDate(movementDate,  30));
  const [search, setSearch] = useState('');
  const [onlyMatchingSign, setOnlyMatchingSign] = useState(true);
  const [onlySameAmount,   setOnlySameAmount]   = useState(false);
  const [selectedId,       setSelectedId]       = useState(null);

  // Reset à chaque réouverture
  useEffect(() => {
    if (open) {
      setFrom(shiftDate(movementDate, -30));
      setTo(shiftDate(movementDate, 30));
      setSearch('');
      setOnlyMatchingSign(true);
      setOnlySameAmount(false);
      setSelectedId(null);
    }
  }, [open, movementDate]);

  const filters = useMemo(() => ({
    from,
    to,
    search: search.length >= 2 ? search : undefined,
    per_page: 100,
    sort: 'operation_date_desc',
  }), [from, to, search]);

  const { data, isLoading, error } = useTransactions(filters);
  const allItems = data?.items ?? (Array.isArray(data) ? data : []);

  const items = useMemo(() => {
    let list = allItems;
    if (onlyMatchingSign) {
      list = list.filter((t) => expectsPositive ? t.amount_cents > 0 : t.amount_cents < 0);
    }
    if (onlySameAmount && movement) {
      list = list.filter((t) => Math.abs(t.amount_cents) === Math.abs(movement.amount_cents ?? 0));
    }
    return list;
  }, [allItems, onlyMatchingSign, onlySameAmount, expectsPositive, movement]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Lier une transaction"
        className="fixed z-50 bg-white shadow-2xl flex flex-col
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh]
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[560px] lg:rounded-none lg:max-h-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">Lier une transaction Qonto</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filtres */}
        <div className="px-4 py-3 space-y-2 border-b border-slate-100 bg-slate-50/50">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              className="w-full rounded-lg border border-slate-300 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500"
              placeholder="Rechercher par libellé, contrepartie…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Du</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                value={from ?? ''}
                onChange={(e) => setFrom(e.target.value || null)}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Au</label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-slate-500"
                value={to ?? ''}
                onChange={(e) => setTo(e.target.value || null)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyMatchingSign}
                onChange={(e) => setOnlyMatchingSign(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-slate-600">
                {expectsPositive ? 'Recettes uniquement' : 'Dépenses uniquement'}
              </span>
            </label>
            {movement?.amount_cents != null && (
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={onlySameAmount}
                  onChange={(e) => setOnlySameAmount(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-slate-600">
                  Même montant ({formatCurrency(Math.abs(movement.amount_cents))})
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex justify-center py-10"><LoadingSpinner /></div>
          )}
          {error && (
            <p className="px-4 py-3 text-sm text-red-600">Impossible de charger les transactions : {error.message}</p>
          )}
          {!isLoading && !error && items.length === 0 && (
            <p className="px-4 py-10 text-sm text-center text-slate-500">
              Aucune transaction ne correspond à ces filtres.
            </p>
          )}
          {!isLoading && items.map((tx) => {
            const isSelected = selectedId === tx.id;
            const cp = tx.clean_counterparty_name ?? tx.counterparty_name ?? tx.label ?? '—';
            const sameAmount = movement?.amount_cents != null &&
              Math.abs(tx.amount_cents) === Math.abs(movement.amount_cents);
            return (
              <button
                key={tx.id}
                type="button"
                onClick={() => setSelectedId(tx.id)}
                className={`w-full text-left px-4 py-2.5 border-b border-slate-50 flex items-center gap-3 transition-colors
                  ${isSelected ? 'bg-sky-50' : 'hover:bg-slate-50'}
                `}
              >
                <div className="shrink-0 w-[68px]">
                  <p className="text-[11px] text-slate-500">
                    {formatDate(tx.settled_at ?? tx.operation_date)}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{cp}</p>
                  {tx.label && tx.label !== cp && (
                    <p className="text-[11px] text-slate-400 truncate">{tx.label}</p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className={`text-sm font-semibold tabular-nums ${
                    tx.amount_cents >= 0 ? 'text-emerald-700' : 'text-red-600'
                  }`}>
                    {tx.amount_cents >= 0 ? '+' : ''}{formatCurrency(tx.amount_cents)}
                  </p>
                  {sameAmount && (
                    <p className="text-[10px] text-emerald-600 font-medium">Match exact</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-4 py-3 bg-white sticky bottom-0">
          <div className="flex gap-2">
            <Button
              type="button"
              className="flex-1"
              disabled={!selectedId}
              onClick={() => selectedId && onLink(selectedId)}
            >
              Lier la transaction
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Movement row ─────────────────────────────────────────────────────────────

function MovementRow({ movement, accountId, trancheId, onEdit }) {
  const linkMutation   = useLinkTransactionToMovement();
  const unlinkMutation = useUnlinkTransactionFromMovement();
  const deleteMutation = useDeleteMovement();

  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const signed  = signedAmount(movement);
  const canLink = !movement.is_historical && !movement.linked_transaction_id;
  const hasLink = Boolean(movement.linked_transaction_id);

  async function handleLink(transactionId) {
    try {
      await linkMutation.mutateAsync({ accountId, trancheId, movementId: movement.id, transactionId });
      setLinkModalOpen(false);
    } catch (err) {
      if (err.status === 400) {
        alert('Ce mouvement est historique et ne peut pas être lié à une transaction Qonto.');
      }
    }
  }

  async function handleUnlink() {
    if (!confirm('Délier cette transaction du mouvement ?')) return;
    await unlinkMutation.mutateAsync({ accountId, trancheId, movementId: movement.id });
  }

  async function handleDelete() {
    if (!confirm('Supprimer ce mouvement ? Cette action est irréversible.')) return;
    try {
      await deleteMutation.mutateAsync({ accountId, trancheId, movementId: movement.id });
    } catch (err) {
      alert(err.message ?? 'Impossible de supprimer ce mouvement.');
    }
  }

  return (
    <>
      <div className="px-4 py-3 border-b border-slate-50 last:border-0">
        <div className="flex items-start gap-3">
          {/* Date + kind */}
          <div className="shrink-0 min-w-[72px]">
            <p className="text-xs text-slate-500">{formatDate(movement.movement_date)}</p>
            <p className={`text-xs font-medium mt-0.5 ${KIND_COLOR[movement.kind] ?? 'text-slate-700'}`}>
              {KIND_LABELS[movement.kind] ?? movement.kind}
            </p>
          </div>

          {/* Label + badges */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-800 truncate">{movement.label ?? '—'}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {movement.is_historical && (
                <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">
                  <History size={9} /> Hors Qonto
                </span>
              )}
              {hasLink && (
                <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">
                  <Link size={9} /> Lié
                </span>
              )}
            </div>
          </div>

          {/* Montant */}
          <p
            className={`text-sm font-semibold tabular-nums shrink-0 ${
              signed >= 0 ? 'text-emerald-700' : 'text-red-600'
            }`}
          >
            {signed >= 0 ? '+' : ''}{formatCurrency(signed)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-2 ml-[84px]">
          <button
            type="button"
            onClick={() => onEdit(movement)}
            className="text-xs text-slate-400 hover:text-slate-700 transition-colors"
          >
            Modifier
          </button>

          {canLink && (
            <button
              type="button"
              onClick={() => setLinkModalOpen(true)}
              disabled={linkMutation.isPending}
              className="text-xs text-sky-500 hover:text-sky-700 transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              <Link size={10} /> Lier une transaction
            </button>
          )}

          {hasLink && (
            <button
              type="button"
              onClick={handleUnlink}
              disabled={unlinkMutation.isPending}
              className="text-xs text-slate-400 hover:text-red-600 transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              <Unlink size={10} /> Délier
            </button>
          )}

          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="text-xs text-slate-300 hover:text-red-500 transition-colors ml-auto disabled:opacity-50"
            aria-label="Supprimer ce mouvement"
          >
            Supprimer
          </button>
        </div>
      </div>

      <LinkTransactionModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onLink={handleLink}
        movement={movement}
      />
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AssociateTrancheDetailPage() {
  const { id: accountId, trancheId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useTranche(accountId, trancheId);
  const updateTranche = useUpdateTranche();

  const [editTrancheOpen,   setEditTrancheOpen]   = useState(false);
  const [movementFormOpen,  setMovementFormOpen]  = useState(false);
  const [editingMovement,   setEditingMovement]   = useState(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Impossible de charger la tranche : {error.message}
      </div>
    );
  }

  if (!data) return null;

  const tranche   = data;
  const movements = [...(data.movements ?? [])].sort(
    (a, b) => (b.movement_date ?? '').localeCompare(a.movement_date ?? '')
  );
  const balance  = tranche.balance_cents ?? 0;
  const isClosed = tranche.status === 'closed';

  async function handleToggleStatus() {
    const newStatus = isClosed ? 'open' : 'closed';
    const confirmMsg = isClosed
      ? 'Réouvrir cette tranche ? Des mouvements pourront à nouveau y être enregistrés.'
      : 'Clôturer cette tranche ? Aucun nouveau mouvement ne pourra y être ajouté.';
    if (!confirm(confirmMsg)) return;
    await updateTranche.mutateAsync({ accountId, trancheId, status: newStatus });
  }

  function handleEditMovement(movement) {
    setEditingMovement(movement);
    setMovementFormOpen(true);
  }

  function handleCloseMovementForm() {
    setMovementFormOpen(false);
    setEditingMovement(null);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate(`/associes/comptes/${accountId}`)}
          className="mt-0.5 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors shrink-0"
          aria-label="Retour au compte associé"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 truncate">{tranche.label}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                isClosed ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {isClosed ? 'Clôturée' : 'Ouverte'}
            </span>
            <span className="text-xs text-slate-400">Ouverte le {formatDate(tranche.opened_at)}</span>
          </div>
        </div>

        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          <Button variant="ghost" className="text-xs gap-1" onClick={() => setEditTrancheOpen(true)}>
            Modifier
          </Button>
          <Button
            variant="ghost"
            className="text-xs gap-1"
            onClick={handleToggleStatus}
            loading={updateTranche.isPending}
          >
            {isClosed ? <Unlock size={13} /> : <Lock size={13} />}
            {isClosed ? 'Réouvrir' : 'Clôturer'}
          </Button>
        </div>
      </div>

      {/* Solde + stats */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h2 className="text-xs font-semibold uppercase text-slate-500 mb-2 tracking-wide">Solde de la tranche</h2>
        <p
          className={`text-3xl font-bold tabular-nums ${
            balance < 0 ? 'text-red-700' : 'text-emerald-700'
          }`}
        >
          {formatCurrency(balance)}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-slate-500">Mouvements</p>
            <p className="text-sm font-semibold text-slate-900">{movements.length}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Liés Qonto</p>
            <p className="text-sm font-semibold text-slate-900">
              {movements.filter((m) => m.linked_transaction_id).length}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Historiques</p>
            <p className="text-sm font-semibold text-slate-900">
              {movements.filter((m) => m.is_historical).length}
            </p>
          </div>
        </div>
      </div>

      {/* Journal des mouvements */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">
            Journal
            <span className="ml-2 text-xs font-normal text-slate-500">({movements.length})</span>
          </h2>
          <Button
            className="text-xs gap-1 py-1.5 px-3"
            onClick={() => { setEditingMovement(null); setMovementFormOpen(true); }}
            disabled={isClosed}
            title={isClosed ? 'La tranche est clôturée' : undefined}
          >
            <Plus size={13} /> Ajouter un mouvement
          </Button>
        </div>

        {isClosed && movements.length === 0 && (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-slate-500">Tranche clôturée — aucun mouvement</p>
          </div>
        )}

        {!isClosed && movements.length === 0 && (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-slate-500">Aucun mouvement enregistré</p>
            <p className="text-xs text-slate-400 mt-1">Ajoutez un premier apport ou une reprise d'antériorité.</p>
          </div>
        )}

        {movements.length > 0 && (
          <div>
            {movements.map((m) => (
              <MovementRow
                key={m.id}
                movement={m}
                accountId={accountId}
                trancheId={trancheId}
                onEdit={handleEditMovement}
              />
            ))}
          </div>
        )}
      </div>

      <AssociateTrancheForm
        open={editTrancheOpen}
        onClose={() => setEditTrancheOpen(false)}
        accountId={accountId}
        tranche={tranche}
      />

      <AssociateMovementForm
        open={movementFormOpen}
        onClose={handleCloseMovementForm}
        accountId={accountId}
        trancheId={trancheId}
        movement={editingMovement}
      />
    </div>
  );
}
